/**
 * Chat Service API Client
 * Handles all chat-related API requests
 */

import apiClient from '../client';
import type {
  Chat,
  ChatCreate,
  ChatUpdate,
  Message,
  MessageCreate,
  MessageUpdate,
  MessageLike,
  MessageSeen,
  MyMention,
  ChatSubscription,
  SubscriptionCreate,
  SubscriptionUpdate,
  NotificationDelivery,
  LikeType,
  MessageListResponse,
  MessageQueryParams,
  ChatMember,
  ChatMemberRole,
} from '../types/chat.types';

// ============================================================================
// Constants
// ============================================================================

export const GENERAL_CHAT_INSTANCE_TYPE = 'general';
export const GENERAL_CHAT_INSTANCE_ID = '00000000-0000-0000-0000-000000000000';

// ============================================================================
// Chat Operations
// ============================================================================

/**
 * Create a new chat
 */
export const createChat = async (data: ChatCreate): Promise<Chat> => {
  const response = await apiClient.post<Chat>('/chat', data);
  return response.data;
};

/**
 * Get chat by ID
 */
export const getChat = async (chatId: string): Promise<Chat> => {
  const response = await apiClient.get<Chat>(`/chat/id/${chatId}`);
  return response.data;
};

/**
 * List all chats for current tenant
 */
export const listChats = async (): Promise<Chat[]> => {
  const response = await apiClient.get<Chat[]>('/chat');
  return response.data;
};

/**
 * Update chat
 */
export const updateChat = async (
  chatId: string,
  data: ChatUpdate
): Promise<Chat> => {
  const response = await apiClient.patch<Chat>(`/chat/id/${chatId}`, data);
  return response.data;
};

/**
 * Delete (soft delete) chat
 */
export const deleteChat = async (chatId: string): Promise<void> => {
  await apiClient.delete(`/chat/id/${chatId}`);
};

// ============================================================================
// Message Operations
// ============================================================================

/**
 * Send a message to a chat
 */
export const sendMessage = async (
  chatId: string,
  data: MessageCreate
): Promise<Message> => {
  const response = await apiClient.post<Message>(
    `/chat/id/${chatId}/messages`,
    data
  );
  return response.data;
};

/**
 * List messages in a chat (with pagination)
 */
export const listMessages = async (
  chatId: string,
  params?: MessageQueryParams
): Promise<MessageListResponse> => {
  const response = await apiClient.get<MessageListResponse>(
    `/chat/id/${chatId}/messages`,
    {
      params: {
        limit: params?.limit,
        offset: params?.offset,
        before_message_id: params?.before_message_id,
        after_message_id: params?.after_message_id,
        include_archived: params?.include_archived,
      },
    }
  );
  return response.data;
};

/**
 * Send a message to an instance (lazy chat initialization)
 * Creates chat automatically if it doesn't exist
 */
export const sendMessageToInstance = async (
  instanceType: string,
  instanceId: string,
  data: MessageCreate
): Promise<Message> => {
  const response = await apiClient.post<Message>(
    `/chat/instance/${instanceType}/${instanceId}/messages`,
    data
  );
  return response.data;
};

/**
 * List messages for an instance (lazy chat initialization with pagination)
 * Returns empty response if chat doesn't exist yet
 */
export const listInstanceMessages = async (
  instanceType: string,
  instanceId: string,
  params?: MessageQueryParams
): Promise<MessageListResponse> => {
  const response = await apiClient.get<MessageListResponse>(
    `/chat/instance/${instanceType}/${instanceId}/messages`,
    {
      params: {
        limit: params?.limit,
        offset: params?.offset,
        before_message_id: params?.before_message_id,
        after_message_id: params?.after_message_id,
        include_archived: params?.include_archived,
      },
    }
  );
  return response.data;
};

/**
 * Get message by ID
 */
export const getMessage = async (messageId: string): Promise<Message> => {
  const response = await apiClient.get<Message>(
    `/chat/messages/id/${messageId}`
  );
  return response.data;
};

/**
 * Update a message
 */
export const updateMessage = async (
  messageId: string,
  data: MessageUpdate
): Promise<Message> => {
  const response = await apiClient.patch<Message>(
    `/chat/messages/id/${messageId}`,
    data
  );
  return response.data;
};

/**
 * Reply to a message
 */
export const replyToMessage = async (
  messageId: string,
  message: string
): Promise<Message> => {
  const response = await apiClient.post<Message>(
    `/chat/messages/id/${messageId}/reply`,
    {
      message,
    }
  );
  return response.data;
};

/**
 * Get replies to a message
 */
export const getMessageReplies = async (
  messageId: string
): Promise<Message[]> => {
  const response = await apiClient.get<Message[]>(
    `/chat/messages/id/${messageId}/replies`
  );
  return response.data;
};

/**
 * Archive a message
 */
export const archiveMessage = async (messageId: string): Promise<void> => {
  await apiClient.post(`/chat/messages/id/${messageId}/archive`);
};

/**
 * Unarchive a message
 */
export const unarchiveMessage = async (messageId: string): Promise<void> => {
  await apiClient.post(`/chat/messages/id/${messageId}/unarchive`);
};

// ============================================================================
// Reaction/Like Operations
// ============================================================================

/**
 * Add a reaction/like to a message
 */
export const addLike = async (
  messageId: string,
  likeType: LikeType
): Promise<MessageLike> => {
  const response = await apiClient.post<MessageLike>(
    `/chat/messages/id/${messageId}/likes`,
    { like_type: likeType }
  );
  return response.data;
};

/**
 * Remove a reaction/like from a message
 */
export const removeLike = async (
  messageId: string,
  likeType: LikeType
): Promise<void> => {
  await apiClient.delete(`/chat/messages/id/${messageId}/likes/${likeType}`);
};

/**
 * List all reactions/likes on a message
 */
export const listLikes = async (messageId: string): Promise<MessageLike[]> => {
  const response = await apiClient.get<MessageLike[]>(
    `/chat/messages/id/${messageId}/likes`
  );
  return response.data;
};

// ============================================================================
// Read Receipt Operations
// ============================================================================

/**
 * Mark a message as seen
 */
export const markMessageAsSeen = async (
  messageId: string
): Promise<MessageSeen> => {
  const response = await apiClient.post<MessageSeen>(
    `/chat/messages/id/${messageId}/seen`
  );
  return response.data;
};

/**
 * Get list of users who saw a message
 */
export const getMessageSeenBy = async (
  messageId: string
): Promise<MessageSeen[]> => {
  const response = await apiClient.get<MessageSeen[]>(
    `/chat/messages/id/${messageId}/seen`
  );
  return response.data;
};

// ============================================================================
// Mention Operations
// ============================================================================

/**
 * Get mentions in a specific message
 */
export const getMessageMentions = async (messageId: string): Promise<any[]> => {
  const response = await apiClient.get<any[]>(
    `/chat/messages/id/${messageId}/mentions`
  );
  return response.data;
};

/**
 * Get my mentions in a chat
 */
export const getMyMentions = async (chatId: string): Promise<MyMention[]> => {
  const response = await apiClient.get<MyMention[]>(
    `/chat/id/${chatId}/my-mentions`
  );
  return response.data;
};

// ============================================================================
// Subscription Operations
// ============================================================================

/**
 * Create a notification subscription for a chat
 */
export const createSubscription = async (
  chatId: string,
  data: SubscriptionCreate
): Promise<ChatSubscription> => {
  const response = await apiClient.post<ChatSubscription>(
    `/chat/id/${chatId}/subscriptions`,
    data
  );
  return response.data;
};

/**
 * List subscriptions for a chat
 */
export const listSubscriptions = async (
  chatId: string
): Promise<ChatSubscription[]> => {
  const response = await apiClient.get<ChatSubscription[]>(
    `/chat/id/${chatId}/subscriptions`
  );
  return response.data;
};

/**
 * Get my subscriptions across all chats
 */
export const getMySubscriptions = async (): Promise<ChatSubscription[]> => {
  const response = await apiClient.get<ChatSubscription[]>(
    '/chat/subscriptions/my'
  );
  return response.data;
};

/**
 * Update a subscription
 */
export const updateSubscription = async (
  subscriptionId: string,
  data: SubscriptionUpdate
): Promise<ChatSubscription> => {
  const response = await apiClient.patch<ChatSubscription>(
    `/chat/subscriptions/id/${subscriptionId}`,
    data
  );
  return response.data;
};

/**
 * Delete a subscription
 */
export const deleteSubscription = async (
  subscriptionId: string
): Promise<void> => {
  await apiClient.delete(`/chat/subscriptions/id/${subscriptionId}`);
};

/**
 * Get delivery history for a subscription
 */
export const getSubscriptionDeliveries = async (
  subscriptionId: string
): Promise<NotificationDelivery[]> => {
  const response = await apiClient.get<NotificationDelivery[]>(
    `/chat/subscriptions/id/${subscriptionId}/deliveries`
  );
  return response.data;
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Get or create a chat for a specific instance
 */
export const getOrCreateChat = async (
  instanceType: string,
  instanceId: string,
  title: string,
  description?: string
): Promise<Chat> => {
  try {
    // Try to find existing chat
    const chats = await listChats();
    const existingChat = chats.find(
      (chat) =>
        chat.instance_type === instanceType && chat.instance_id === instanceId
    );

    if (existingChat) {
      return existingChat;
    }

    // Create new chat if not found
    return await createChat({
      instance_type: instanceType,
      instance_id: instanceId,
      title,
      description,
    });
  } catch (error) {
    console.error('Error getting or creating chat:', error);
    throw error;
  }
};

/**
 * Get global chat (instance_type: 'general', instance_id: '00000000-0000-0000-0000-000000000000')
 * @deprecated Use lazy initialization with listGeneralChatMessages instead
 */
export const getGlobalChat = async (): Promise<Chat> => {
  return getOrCreateChat(
    'general',
    '00000000-0000-0000-0000-000000000000',
    'Global Chat',
    'General discussion for all users'
  );
};

/**
 * List messages for general chat (lazy initialization with pagination)
 * Returns empty response if chat doesn't exist yet
 */
export const listGeneralChatMessages = async (
  params?: MessageQueryParams
): Promise<MessageListResponse> => {
  return listInstanceMessages(
    GENERAL_CHAT_INSTANCE_TYPE,
    GENERAL_CHAT_INSTANCE_ID,
    params
  );
};

/**
 * Send message to general chat (lazy initialization)
 * Creates chat automatically if it doesn't exist
 */
export const sendGeneralChatMessage = async (
  data: MessageCreate
): Promise<Message> => {
  return sendMessageToInstance(
    GENERAL_CHAT_INSTANCE_TYPE,
    GENERAL_CHAT_INSTANCE_ID,
    data
  );
};

// ============================================================================
// Chat Types System - New Endpoints
// ============================================================================

/**
 * Find existing 1-1 chat between current user and another user, or create it
 * Returns the chat (existing or newly created)
 */
export const findOrCreateOneToOne = async (
  otherUserId: string
): Promise<Chat> => {
  const response = await apiClient.post<Chat>(
    '/chat/find-or-create-one-to-one',
    { other_user_id: otherUserId }
  );
  return response.data;
};

/**
 * Auto-join general chat (idempotent)
 * Adds current user as member of general tenant chat
 */
export const joinGeneralChat = async (): Promise<ChatMember> => {
  const response = await apiClient.post<ChatMember>('/chat/general/join');
  return response.data;
};

// ============================================================================
// Member Management Operations
// ============================================================================

/**
 * Get all members of a chat
 */
export const listMembers = async (chatId: string): Promise<ChatMember[]> => {
  const response = await apiClient.get<ChatMember[]>(
    `/chat/id/${chatId}/members`
  );
  return response.data;
};

/**
 * Add members to a group chat (admin/owner only)
 */
export const addMembers = async (
  chatId: string,
  userIds: string[]
): Promise<ChatMember[]> => {
  const response = await apiClient.post<ChatMember[]>(
    `/chat/id/${chatId}/members`,
    { user_ids: userIds }
  );
  return response.data;
};

/**
 * Remove a member from a group chat (admin/owner only)
 */
export const removeMember = async (
  chatId: string,
  userId: string
): Promise<void> => {
  await apiClient.delete(`/chat/id/${chatId}/members/${userId}`);
};

/**
 * Update member role (owner only)
 * Can promote to admin or demote to member
 */
export const updateMemberRole = async (
  chatId: string,
  userId: string,
  role: ChatMemberRole.ADMIN | ChatMemberRole.MEMBER
): Promise<ChatMember> => {
  const response = await apiClient.patch<ChatMember>(
    `/chat/id/${chatId}/members/${userId}/role`,
    { role }
  );
  return response.data;
};
